import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { 
  Gem, 
  Pen, 
  Check, 
  Target,
  Shield,
  Clock,
  Eye,
  Zap,
  Sparkles
} from 'lucide-react';
import './Techniques.css';

const getHybridTechnique = (language) => ({
  id: 'hybrid-eyebrow',
  name: language === 'de' ? 'Saphir FUE & DHI Hybrid' : 'Sapphire FUE & DHI Hybrid',
  subtitle: language === 'de' ? 'Das Beste aus beiden Welten – individuell kombiniert' : 'The best of both worlds – individually combined',
  icon: Sparkles,
  image: 'https://customer-assets.emergentagent.com/job_5a750e2c-9afa-457e-8878-f20f1025e07d/artifacts/876q7a14_saphir_fue.webp',
  description: language === 'de'
    ? 'Bei der Augenbrauentransplantation kombinieren wir die Vorteile beider Techniken: Die Saphir-FUE-Methode ermöglicht präzise Mikro-Inzisionen für schnellere Heilung, während die DHI-Technik eine optimale Kontrolle über Haarrichtung und -winkel bietet. So erzielen wir natürliche Ergebnisse, die perfekt zu Ihrer Gesichtsform passen.'
    : 'For eyebrow transplantation, we combine the advantages of both techniques: The Sapphire FUE method enables precise micro-incisions for faster healing, while the DHI technique offers optimal control over hair direction and angle. This way we achieve natural results that perfectly match your face shape.',
  idealFor: language === 'de' ? 'Optimale Ergebnisse durch Technik-Kombination' : 'Optimal results through technique combination',
  features: [
    { icon: Gem, text: language === 'de' ? 'Saphirklingen für feinste Schnitte' : 'Sapphire blades for finest cuts' },
    { icon: Pen, text: language === 'de' ? 'DHI-Implanter für präzise Platzierung' : 'DHI implanter for precise placement' },
    { icon: Target, text: language === 'de' ? 'Optimale Winkelkontrolle' : 'Optimal angle control' },
    { icon: Eye, text: language === 'de' ? 'Natürliche Wuchsrichtung' : 'Natural growth direction' },
    { icon: Shield, text: language === 'de' ? 'Minimale Gewebeschädigung' : 'Minimal tissue damage' },
    { icon: Zap, text: language === 'de' ? 'Schnelle Heilung' : 'Fast healing' },
  ],
  stats: [
    { value: '0.8mm', label: language === 'de' ? 'Mikro-Inzisionen' : 'Micro-incisions' },
    { value: '95%+*', label: language === 'de' ? 'Anwachsrate' : 'Growth rate' },
    { value: '5-10', label: language === 'de' ? 'Tage Heilung' : 'Days healing' },
  ],
  benefits: language === 'de'
    ? ['Individuell angepasste Technik', 'Natürliche Augenbrauenform', 'Präzise Haarrichtung', 'Optimale Verdichtung möglich', 'Schnelle Heilung', 'Minimale Schwellung']
    : ['Individually adapted technique', 'Natural eyebrow shape', 'Precise hair direction', 'Optimal densification possible', 'Fast healing', 'Minimal swelling']
});

const EyebrowTechniques = ({ onOpenConsultation }) => {
  const { features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  
  const technique = getHybridTechnique(language);
  
  // Only show on eyebrow transplant pages
  if (features?.analysisType !== 'eyebrow') {
    return null;
  }

  return (
    <section id="techniken-augenbrauen" className={`relative py-16 sm:py-24 overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0a0a0a]' : 'bg-white'
    }`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/4 right-0 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/5'
        }`} />
        <div className={`absolute bottom-1/4 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] rounded-full blur-[60px] ${
          isDark ? 'bg-[#5A8BA6]/10' : 'bg-[#7BA7C2]/5'
        }`} />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4 sm:mb-6 ${
            isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20'
          }`}>
            <Sparkles className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
            <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
              {language === 'de' ? 'Modernste Technologie' : 'Latest Technology'}
            </span>
          </div>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Unsere ' : 'Our '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Technik' : 'Technique'}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-base sm:text-lg px-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' 
              ? 'Wir kombinieren die besten Eigenschaften von Saphir FUE und DHI für optimale Ergebnisse bei Ihrer Augenbrauentransplantation.'
              : 'We combine the best features of Sapphire FUE and DHI for optimal results in your eyebrow transplantation.'}
          </p>
        </div>

        {/* Single Hybrid Technique Card */}
        <div 
          className={`group relative rounded-2xl border transition-all duration-500 overflow-hidden ${
            isDark 
              ? 'bg-white/[0.02] border-white/10 hover:border-[#7BA7C2]/30 hover:bg-white/[0.05]'
              : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30 shadow-lg'
          }`}
        >
          {/* Stacked Layout - Image on top, content below */}
          <div className="flex flex-col">
            {/* Image Row */}
            <div className="relative overflow-hidden">
              <img 
                src={technique.image} 
                alt={`${technique.name} Augenbrauentransplantation Methode - Dion Hair Clinic`}
                loading="lazy"
                className="w-full h-64 sm:h-80 object-cover object-center group-hover:scale-105 transition-transform duration-500"
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0a0a0a]/60 via-transparent' : 'from-white/40 via-transparent'} to-transparent`} />
              
              {/* Badge on Image */}
              <div className="absolute bottom-4 left-4 right-4">
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium backdrop-blur-md ${
                  isDark ? 'bg-black/50 text-[#7BA7C2] border border-[#7BA7C2]/30' : 'bg-white/90 text-[#5A8BA6] border border-[#5A8BA6]/30'
                }`}>
                  <Sparkles className="w-4 h-4" />
                  {technique.idealFor}
                </span>
              </div>
            </div>

            {/* Content Row */}
            <div className="p-6 lg:p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <h3 className={`text-2xl lg:text-3xl font-bold mb-2 transition-colors duration-300 ${
                  isDark ? 'text-white group-hover:text-[#7BA7C2]' : 'text-gray-900 group-hover:text-[#5A8BA6]'
                }`}>
                  {technique.name}
                </h3>
                <p className={`text-sm ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>{technique.subtitle}</p>
              </div>

              <p className={`text-sm leading-relaxed mb-6 text-center max-w-3xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {technique.description}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 mb-6 max-w-md mx-auto">
                {technique.stats.map((stat, statIndex) => {
                  const hasAsterisk = stat.value.includes('*');
                  const displayValue = hasAsterisk ? stat.value.replace('*', '') : stat.value;
                  
                  return (
                    <div 
                      key={statIndex}
                      className={`text-center p-3 rounded-xl border ${
                        isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border-gray-100'
                      }`}
                    >
                      <div className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {displayValue}
                        {hasAsterisk && <span className={`text-[10px] align-super ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>*</span>}
                      </div>
                      <div className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{stat.label}</div>
                    </div>
                  );
                })}
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
                {technique.features.map((feature, featureIndex) => {
                  const FeatureIcon = feature.icon;
                  return (
                    <div 
                      key={featureIndex}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border ${
                        isDark ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-100'
                      }`}
                    >
                      <FeatureIcon className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                      <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{feature.text}</span>
                    </div>
                  );
                })}
              </div>

              {/* Benefits List */}
              <div className={`p-4 rounded-xl border ${
                isDark ? 'bg-[#7BA7C2]/5 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/5 border-[#5A8BA6]/20'
              }`}>
                <h4 className={`font-semibold mb-3 text-sm text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'de' ? 'Ihre Vorteile:' : 'Your Benefits:'}
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {technique.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center gap-2">
                      <Check className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footnote */}
        <p className={`text-center text-xs mt-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          {language === 'de' 
            ? '* Die Anwachsrate kann je nach individuellen Faktoren variieren. Ergebnisse basieren auf klinischen Beobachtungen.'
            : '* Growth rate may vary depending on individual factors. Results are based on clinical observations.'}
        </p>
      </div>
    </section>
  );
};

export default EyebrowTechniques;
